import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { GroupSelect } from "@/components/GroupSelect";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

export function QuickAssignDialog({ open, onOpenChange, client, onSaved }) {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && client) {
      setSelected(client.assigned_user_ids || []);
      api.get("/users").then((r) => setMembers(r.data)).catch(() => setMembers([]));
    }
  }, [open, client]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/clients/${client.id}/assignments`, { assigned_user_ids: selected });
      toast.success("Assignment updated");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update assignment");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-sm" data-testid="quick-assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-[#002FA7]" /> Assign team members
          </DialogTitle>
          <DialogDescription>
            {client?.name} — only assigned members and admins can access this client.
          </DialogDescription>
        </DialogHeader>

        <div className="py-1">
          {members.length === 0 ? (
            <p className="text-sm text-gray-400 py-3">Loading team members…</p>
          ) : (
            <GroupSelect groups={members} selected={selected} onChange={setSelected} testidPrefix="quick-assignee" />
          )}
          <p className="text-xs text-gray-400 mt-2">
            {selected.length > 0
              ? `${selected.length} member${selected.length === 1 ? "" : "s"} will have access.`
              : "No one assigned — access falls back to the client's access groups."}
          </p>
        </div>

        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button type="button" onClick={save} disabled={saving} data-testid="quick-assign-save-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
